import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import APIErrorsHandlingUtils from 'utils/APIErrorsHandlingUtils';

/**
 * Фабрика зрізів сховища для розділів власного бекенда.
 *
 * Раніше кожен розділ був четвіркою файлів (actionTypes, actionCreators,
 * reducer, selectors) плюс thunk, який при помилці писав у консоль і на цьому
 * зупинявся. У сховищі не лишалося ані ознаки завантаження, ані ознаки збою,
 * тому сторінка не могла відрізнити «ще вантажиться» від «запит упав» і
 * показувала лоадер назавжди.
 *
 * Тут у кожного розділу чотири поля:
 *   data     — самі дані
 *   status   — 'idle' | 'loading' | 'succeeded' | 'failed'
 *   error    — вид помилки (див. APIErrorsHandlingUtils.classify) або null
 *   language — мова, якою ці дані завантажені
 *
 * Мова в сховищі потрібна тому, що переклад у CMS — це окремий запис: після
 * перемикання мови розділ треба запитати наново, і саме за цим полем useContent
 * бачить, що наявні дані вже не тієї мови.
 *
 * Готового тексту помилки в сховищі немає навмисно: мову інтерфейсу можна
 * перемкнути вже після збою, тож текст добирається під час рендеру.
 */
export function createContentSlice({ name, fetcher, transform, initialData = [] }) {
  const load = createAsyncThunk(`${name}/load`, async (language, { rejectWithValue }) => {
    const [error, data] = await fetcher(language);

    if (error) return rejectWithValue(APIErrorsHandlingUtils.classify(error));

    return transform ? transform(data) : data;
  });

  const slice = createSlice({
    name,
    initialState: { data: initialData, status: 'idle', error: null, language: null },
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(load.pending, (state, { meta }) => {
          state.status = 'loading';
          state.error = null;
          state.language = meta.arg;
        })
        .addCase(load.fulfilled, (state, { payload, meta }) => {
          // Відповідь на застарілий запит ігнорується: якщо мову перемкнули,
          // поки українська версія ще їхала, вона не має затерти англійську.
          if (meta.arg !== state.language) return;

          state.status = 'succeeded';
          state.data = payload;
          state.error = null;
        })
        .addCase(load.rejected, (state, { payload, meta }) => {
          if (meta.arg !== state.language) return;

          state.status = 'failed';
          // Попередні дані лишаємо: якщо оновлення впало, показати старий
          // список краще, ніж стерти екран.
          state.error = payload || { kind: 'unknown', status: null };
        });
    },
  });

  return { reducer: slice.reducer, load };
}
